import { create } from 'zustand';
import produce from 'immer';
import { persist } from 'zustand/middleware';


// Create the Zustand store
export const usePlayerStore = create((set) => ({
    pInfo: {},
    health: { current: 0, max: 0 },
    mana: { current: 0, max: 0 },
    experience: {},
    gold: 0,
    statPoints: 0,
    playerStats: {
        level: 0,
        str: 0,
        mstr: 0,
        agi: 0,
        magi: 0,
        tog: 0,
        mtog: 0,
        intel: 0,
        mintel: 0,
        wis: 0,
        mwis: 0,
        chr: 0,
        mchr: 0,
        armour: 0,
    },
    setInfo: (pInfo) => set({pInfo}),
    updatePlayerStats: (data) => updatePlayerStats(data, set),
    setHealth: (current, max) => set({ health: { current, max } }),
    setMana: (current, max) => set({ mana: { current, max } }),
    setExperience: (current, max) => set({ experience: { current, max } }),
    setGold: (gold) => set({ gold: gold }),
    setStatPoints: (sp) => set({ statPoints: sp })
}));

const MAX_MESSAGES = 1500;

// Line break regex for normalizing \r\n and \n\r to \n
const LINE_BREAK_REGEX = /\r\n|\n\r/g;

// Check if content contains newlines (used to skip splitting for single-line messages)
const hasNewlines = (content) => {
    if (!content) return false;
    for (let i = 0; i < content.length; i++) {
        const text = content[i].text;
        if (text.indexOf('\n') !== -1 || text.indexOf('\r') !== -1) return true;
    }
    return false;
};

// Split message content into lines - done ONCE at creation time, not on render
const splitMessageIntoLines = (content) => {
    if (!content) return [content];
    
    const lines = [];
    let currentLine = [];
    
    content.forEach((element) => {
        const text = element.text;
        const normalized = (text.indexOf('\r\n') !== -1 || text.indexOf('\n\r') !== -1)
            ? text.replace(LINE_BREAK_REGEX, '\n')
            : text;
        const parts = normalized.split('\n');
        
        if (parts[0]) {
            currentLine.push({ ...element, text: parts[0] });
        }
        
        for (let i = 1; i < parts.length; i++) {
            if (currentLine.length > 0) {
                lines.push(currentLine);
            }
            currentLine = parts[i] ? [{
                ...element,
                text: parts[i],
                key: `${element.key || 'el'}-${i}`
            }] : [];
        }
    });
    
    if (currentLine.length > 0) {
        lines.push(currentLine);
    }
    
    return lines.length > 0 ? lines : [content];
};

// Message counter for unique keys
let messageCounter = 0;
let messageCounter2 = 0;

export const useMessageStore = create(
    persist(
        (set) => ({
            messages: [],  // Now stores pre-processed lines ready for render
            messageVersion: 0,  // Increments on each message add - used for scroll effect dependency
            messages2: [],  // Second console messages (separate history)
            messageVersion2: 0,  // Second console version counter
            showTimestamps: false,
            showSecondConsole: false,  // Whether second console is visible
            consoleFontFamily: 'M PLUS 1 Code',  // Default monospace font
            consoleFontSize: 'medium',  // small, medium, large, xlarge
            addMessage: (content, timestamp = null, timestampDisplay = null) => {
                set((state) => {
                    // Pre-split lines at creation time (O(1) for new message only)
                    const lines = hasNewlines(content) 
                        ? splitMessageIntoLines(content)
                        : [content];
                    
                    // Create line objects ready for render - no processing needed later
                    const baseId = messageCounter++;
                    const newLines = lines.map((lineContent, lineIndex) => ({
                        content: lineContent,
                        timestampDisplay,
                        key: `${baseId}-${lineIndex}`
                    }));
                    
                    const newMessages = [...state.messages, ...newLines];
                    
                    // Keep only the newest MAX_MESSAGES lines (FIFO cleanup)
                    const trimmedMessages = newMessages.length > MAX_MESSAGES
                        ? newMessages.slice(-MAX_MESSAGES)
                        : newMessages;
                    
                    return { messages: trimmedMessages, messageVersion: state.messageVersion + 1 };
                });
            },
            addMessage2: (content, timestamp = null, timestampDisplay = null) => {
                set((state) => {
                    // Pre-split lines at creation time (O(1) for new message only)
                    const lines = hasNewlines(content) 
                        ? splitMessageIntoLines(content)
                        : [content];
                    
                    // Create line objects ready for render - no processing needed later
                    const baseId = messageCounter2++;
                    const newLines = lines.map((lineContent, lineIndex) => ({
                        content: lineContent,
                        timestampDisplay,
                        key: `c2-${baseId}-${lineIndex}`
                    }));
                    
                    const newMessages = [...state.messages2, ...newLines];
                    
                    // Keep only the newest MAX_MESSAGES lines (FIFO cleanup)
                    const trimmedMessages = newMessages.length > MAX_MESSAGES
                        ? newMessages.slice(-MAX_MESSAGES)
                        : newMessages;
                    
                    return { messages2: trimmedMessages, messageVersion2: state.messageVersion2 + 1 };
                });
            },
            setShowTimestamps: (show) => set({ showTimestamps: show }),
            setShowSecondConsole: (show) => set({ showSecondConsole: show }),
            setConsoleFontFamily: (font) => set({ consoleFontFamily: font }),
            setConsoleFontSize: (size) => set({ consoleFontSize: size }),
            clearMessages: () => set({ messages: [], messageVersion: 0 }),
            clearMessages2: () => set({ messages2: [], messageVersion2: 0 }),
            clearAllMessages: () => set({ 
                messages: [], 
                messageVersion: 0, 
                messages2: [], 
                messageVersion2: 0 
            }),
        }),
        {
            name: 'message-store',
            partialize: (state) => ({ 
                showTimestamps: state.showTimestamps,
                showSecondConsole: state.showSecondConsole,
                consoleFontFamily: state.consoleFontFamily,
                consoleFontSize: state.consoleFontSize,
            }),
        }
    )
);

const updateInventory = (data, set) => {
    if (data.includes(';')) {
        const parse = data.split(';');
        const [invCount, invMax] = parse[0].split(',').map(Number);
        const itemList = [];

        for (let i = 1; i < parse.length - 1; i++) {
            const itemData = parse[i].split(',');
            const picId = parseInt(itemData[0]);
            const colour = itemData[1].replace('{c}', '#');
            const name = itemData[2];
            const qty = parseInt(itemData[3]);
            const uni = itemData[4];
            const type = itemData[5];
            const slot = itemData[6];
            let tag = '';
            let conItems = 0;
            let cap = 0;
            let maxSockets = 0;
            let usedSockets = 0;
            if (itemData.length > 8) {
                tag = itemData[7];
            }

            if (itemData.length >= 12) {
                conItems = parseInt(itemData[8]);
                cap = parseInt(itemData[9]);
                maxSockets = parseInt(itemData[10]);
                usedSockets = parseInt(itemData[11]);
            }
            itemList.push({ picId, colour, name, qty, uni, type, slot, tag, conItems, cap, maxSockets, usedSockets });
        }
        set({ inventory: { invCount, invMax, itemList } });
    }
};


// Define the updateEquipment function
const updateEquipment = (data, set) => {
    const info = data.split(';');
    const equip = [];

    for (let i = 0; i < info.length - 1; i++) {
        const eInfo = info[i].split(',');
        const picId = parseInt(eInfo[0]);
        const type = eInfo[1];
        const colour = eInfo[2].replace('{c}', '#');
        const name = eInfo[3];
        const slot = eInfo[4];
        equip.push({ picId, type, colour, name, slot });
    }

    set({ equipment: equip });
};

// Create the Zustand store
export const useInventoryStore = create((set) => ({
    inventory: {
        invCount: 0,
        invMax: 0,
        itemList: [],
    },
    equipment: [],
    inventoryInfo: {},
    updateInventory: (data) => updateInventory(data, set),
    updateEquipment: (data) => updateEquipment(data, set),
    updateInventoryInfo: (inventoryInfo) => set({ inventoryInfo: inventoryInfo })
}));


// Define the updatePlayerStats function
function updatePlayerStats(data, set) {
    const stats = data.split(":");
    const level = parseInt(stats[0]);
    const str = parseInt(stats[1]);
    const mstr = parseInt(stats[2]);
    const agi = parseInt(stats[3]);
    const magi = parseInt(stats[4]);
    const tog = parseInt(stats[5]);
    const mtog = parseInt(stats[6]);
    const intel = parseInt(stats[7]);
    const mintel = parseInt(stats[8]);
    const wis = parseInt(stats[9]);
    const mwis = parseInt(stats[10]);
    const chr = parseInt(stats[11]);
    const mchr = parseInt(stats[12]);
    const armour = parseInt(stats[13]);
    const playerData = { level: level, str: str, mstr: mstr, agi: agi, magi: magi, tog: tog, mtog: mtog, intel: intel, mintel: mintel, wis: wis, mwis: mwis, chr: chr, mchr: mchr, armour: armour };

    set({ playerStats: playerData });
}







// Define the updateShop function
const updateShop = (data, set) => {
    if (data.includes(';')) {
        const split = data.split(';');
        const id = split[0];
        const list = [];
        for (let i = 1; i < split.length - 1; i++) {
            const itemData = split[i].split(',');
            const itemId = itemData[0];
            const picId = itemData[1];
            const colour = itemData[2].replace('{c}', '#');
            const curSockets = parseInt(itemData[3]);
            const maxSockets = parseInt(itemData[4]);
            const curCap = parseInt(itemData[5]);
            const maxCap = parseInt(itemData[6]);
            const name = itemData[7];
            const type = itemData[8];
            const type2 = itemData[9];
            const cost = parseInt(itemData[10]);
            let amount = 1;
            let isUnique = false;
            let tag = "";
            if (itemData.length >= 13) {
                amount = parseInt(itemData[11]);
                isUnique = itemData[12];
                tag = itemData[13];
            }

            list.push({
                itemId,
                picId,
                colour,
                curSockets,
                maxSockets,
                curCap,
                maxCap,
                name,
                type,
                type2,
                cost,
                amount,
                isUnique,
                tag
            });
        }

        set(produce(state => {
            if (state.shopList.shopId !== id || JSON.stringify(state.shopList.list) !== JSON.stringify(list)) {
                state.shopList = { shopId: id, list: list };
            }
        }));
    }
};

// Create the Zustand store
export const useShopStore2 = create((set) => ({
    shopList: {
        shopId: '',
        list: [],
    },
    updateShop: (data) => updateShop(data, set),
}));


const useShopStore = create((set) => ({
    shopList: { id: null, list: [] },
    setShopList: (data) => set(() => ({ shopList: parseShopList(data) })),
}));

const parseShopList = (data) => {
    if (!data || !data.includes(';')) return { id: null, list: [] };

    const [id, ...items] = data.split(';');
    const list = items.slice(0, -1).map(item => {
        const [
            itemId, picId, colourRaw, curSockets, maxSockets,
            curCap, maxCap, name, type, type2, cost,
            amount = 1, isUnique = false, tag = ""
        ] = item.split(',');

        return {
            itemId,
            picId,
            colour: colourRaw.replace('{c}', '#'),
            curSockets: parseInt(curSockets),
            maxSockets: parseInt(maxSockets),
            curCap: parseInt(curCap),
            maxCap: parseInt(maxCap),
            name,
            type,
            type2,
            cost: parseInt(cost),
            amount: parseInt(amount),
            isUnique: isUnique === 'true',
            tag,
        };
    });

    return { id, list };
};

export const useCraftStore = create((set) => ({
    showCraft: false,
    craftList: { id: null, list: [] },
    craftInfo: {},
    setCraftList: (data) => set(() => ({ craftList: parseShopList(data) })),
    setShowCraft: (showCraft) => set({ showCraft }),
    setCraftInfo: (craftInfo) => set({ craftInfo }),
}));

export const useAbilityStore = create((set) => ({
    show: false,
    id: null,
    points: 0,
    abilities: [],
    abilityInfo: null,
    setId: (id) => set({ id }),
    setPoints: (points) => set({ points }),
    setAbilities: (data) => set(() => ({ abilities: data})),
    setShow: (show) => set({ show }),
    setAbilityInfo: (abilityInfo) => set({ abilityInfo })
}));

export const useLearnStore = create((set) => ({
    show: false,
    id: null,
    points: 0,
    abilities: [],
    abilityInfo: null,
    setId: (id) => set({ id }),
    setPoints: (points) => set({ points }),
    setAbilities: (data) => set(() => ({ abilities: data})),
    setShow: (show) => set({ show }),
    setAbilityInfo: (abilityInfo) => set({ abilityInfo })
}));

export const useVaultStore = create((set) => ({
    show: false,
    id: null,
    balance: 0,
    upgradeCost: 0,
    inventory: {
        invCount: 0,
        invMax: 0,
        itemList: [],
    },
    itemInfo: {},
    setShow: (show) => set({ show }),
    setId: (id) => set({ id }),
    setBalance: (balance) => set({ balance }),
    setUpgradeCost: (upgradeCost) => set({ upgradeCost }),
    updateVault: (data) => updateInventory(data, set),
    setItemInfo: (itemInfo) => set({ itemInfo })
}));

export const useTradeStore = create((set) => ({
    show: true,
    setShow: (show) => set({ show }),
}));

export const useDialogStore = create((set) => ({
    show: true,
    msg: "",
    setShow: (show) => set({ show }),
    setMsg: (msg) => set({ msg }),
}));




export const useActionsStore = (accountId) => create(persist(
  (set) => ({
    actions: [
        [
          { key: 'F1', name: "Attack", cmd: "/a" },
          { key: 'F2', name: "Stop Attack", cmd: "/stop" },
          { key: 'F3', name: "Edit Hotkeys", cmd: "/hotkey" },
          { key: 'F4', name: "Help", cmd: "/help" },
          { key: 'F5', name: "", cmd: "" },
          { key: 'F6', name: "", cmd: "" },
          { key: 'F7', name: "", cmd: "" },
          { key: 'F8', name: "", cmd: "" },
          { key: 'F9', name: "", cmd: "" },
        ],
        [
          { key: 'F1', name: "", cmd: "" },
          { key: 'F2', name: "", cmd: "" },
          { key: 'F3', name: "", cmd: "" },
          { key: 'F4', name: "", cmd: "" },
          { key: 'F5', name: "", cmd: "" },
          { key: 'F6', name: "", cmd: "" },
          { key: 'F7', name: "", cmd: "" },
          { key: 'F8', name: "", cmd: "" },
          { key: 'F9', name: "", cmd: "" },
        ],
        [
          { key: 'F1', name: "", cmd: "" },
          { key: 'F2', name: "", cmd: "" },
          { key: 'F3', name: "", cmd: "" },
          { key: 'F4', name: "", cmd: "" },
          { key: 'F5', name: "", cmd: "" },
          { key: 'F6', name: "", cmd: "" },
          { key: 'F7', name: "", cmd: "" },
          { key: 'F8', name: "", cmd: "" },
          { key: 'F9', name: "", cmd: "" },
        ],
        [
          { key: 'F1', name: "", cmd: "" },
          { key: 'F2', name: "", cmd: "" },
          { key: 'F3', name: "", cmd: "" },
          { key: 'F4', name: "", cmd: "" },
          { key: 'F5', name: "", cmd: "" },
          { key: 'F6', name: "", cmd: "" },
          { key: 'F7', name: "", cmd: "" },
          { key: 'F8', name: "", cmd: "" },
          { key: 'F9', name: "", cmd: "" },
        ],
      ],
    setActions: (actions) => set({ actions }),
  }),
  {
    name: `actions-storage-${accountId}`, // Use account ID to create a unique storage key
    getStorage: () => localStorage, // (optional) by default 'localStorage' is used
  }
));

